import { useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS, apiRequest } from '@/lib/api';
import { useState, useEffect } from 'react';

interface SearchParams {
  query: string;
  dashboard?: number;
  project?: number;
}

export function useSearch({ query, dashboard, project }: SearchParams) {
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return useQuery({
    queryKey: ['search', debouncedQuery, dashboard, project],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) {
        return [];
      }

      const params = new URLSearchParams({ q: debouncedQuery });
      if (dashboard !== undefined) params.append('dashboard', String(dashboard));
      if (project !== undefined) params.append('project', String(project));

      const response = await apiRequest(
        `${API_ENDPOINTS.TASKS.SEARCH}?${params.toString()}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: 'Search failed'
        }));
        throw new Error(errorData.error || 'Search failed');
      }

      return response.json();
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 30000, // 30 seconds - search results can be slightly stale
  });
}
